/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import initialize1Url from "url:../assets/images/create_initialize_1.png";
import initialize2Url from "url:../assets/images/create_initialize_2.png";
import initialize3Url from "url:../assets/images/create_initialize_3.png";
import inputspanel1Url from "url:../assets/images/create_inputspanel_1.png";
import inputspanel2Url from "url:../assets/images/create_inputspanel_2.png";
import inputspanel3Url from "url:../assets/images/create_inputspanel_3.png";
import inputspanel4Url from "url:../assets/images/create_inputspanel_4.png";
import inputspanel5Url from "url:../assets/images/create_inputspanel_5.png";
import transformprocess2Url from "url:../assets/images/create_transformprocess_2.png";
import transformprocess3Url from "url:../assets/images/create_transformprocess_3.png";
import transformprocess4Url from "url:../assets/images/create_transformprocess_4.png";
import transformprocess5Url from "url:../assets/images/create_transformprocess_5.png";
import transformprocess6Url from "url:../assets/images/create_transformprocess_6.png";
import transformprocess7Url from "url:../assets/images/create_transformprocess_7.png";

export const creatingTransformContent = `
## Summary

This tutorial walks you through the complete process of creating a transformation pipeline (mapping) from scratch in Dexter. You'll learn two different workflows:

1. **Using the Initialization Modal** - Best for first-time setup or when starting fresh
2. **Using the Inputs Side Panel** - Best when adding to an existing mapping, or when relevant input collections already exist

This tutorial uses these supported transform types to demonstrate the complete workflow.

### Workflow 1: Initialization Modal

This workflow uses the Initialization Modal to upload files and automatically create input nodes.

#### Step 1: Upload files to initialization modal

When you first open the Transform Engine with an empty canvas, the Initialization Modal appears automatically. Select "Upload Files and Recommend Transform" to begin.

<img src="${initialize1Url}" alt="Initialization Dialog 1" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Click "Browse files" or drag and drop your source files into the upload area. For this example, we're uploading Protobuf and XML files.

**What happens:**
- Files are uploaded to the server
- Dexter's AI classifier analyzes each file
- File types are automatically detected
- Status changes from "Checking..." to "Completed"

**Tips:**
- Wait for all files to show "Completed" status
- Verify the detected file types in the dropdown
- Override any incorrect type detections if needed

<img src="${initialize2Url}" alt="Initialization Dialog 2" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Confirm that the file count is correct and click "Finish."

<img src="${initialize3Url}" alt="Initialization Dialog 2" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Result:**
- Input nodes are automatically created on the canvas
- Nodes are positioned in an organized layout
- Each node displays the parsed file structure
- You're ready to add transforms

#### Step 2: Add transform nodes

<img src="${transformprocess2Url}" alt="Transform Process 2" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Now that your input nodes are on the canvas, reposition them to your liking and add transform nodes to convert the data between formats.

Click the transform icon (exchange arrows) in the left control bar to open the transforms side panel.

<img src="${transformprocess3Url}" alt="Transform Process 3" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**For this example:**
- We have Protobuf input files
- We want to convert them to XML, both the full file and an individual data slice
- Drag "Proto to XML (full)" transform
- Drag "Proto to XML (granular)" transform

**Understanding transform types:**
- **Full-file** transforms operate on the entire file
- **Granular** transforms work with specific data fields
- Each transform shows its input/output format compatibility

Drag the transform node from the side panel onto the canvas. Position it between your input node and where your output will be.

#### Step 3: Connect input nodes to transform nodes

<img src="${transformprocess4Url}" alt="Transform Process 4" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

#### Step 4: Add output nodes

Output nodes capture the transformed data so you can download it.

<img src="${transformprocess5Url}" alt="Transform Process 5" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Click the output icon (arrow pointing right) in the left control bar to add an output node.

<img src="${transformprocess6Url}" alt="Transform Process 6" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

Click the Composer Node icon in the left control bar to add a composer node, which allows reordering of data slices in transform output. See the Composer Nodes section for more details.

**Automatic positioning:**
- Output node is intelligently placed to the right of your transform
- If multiple transforms exist, the output positions near the first available one
- You can manually reposition if needed

**How many outputs:**
- Add one output node per transform
- Each output node corresponds to a single downloadable file
- Multiple transform nodes can be routed to a single output node to "compose" different outputs into a single file

#### Step 5: Connect transform nodes to output nodes

Complete the pipeline by connecting transforms to outputs.

<img src="${transformprocess7Url}" alt="Transform Process 7" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Connecting transforms to outputs:**
1. Click and drag from the right side of your transform node
2. Drag to the left side of the output node
3. Release to create the connection

**What happens next:**
- The transformed data is routed to the output node
- The download button becomes active
- Transformed data slices appear in Composer Nodes and can be reordered
- Transformation result is ready to download

#### Step 6: Download transformed data from output nodes

Download your transformed data from the output node.

**Download options:**
- **Single download**: Click the download icon on an individual output node
- **Batch download**: Use the ZIP download button in the control bar to download all outputs at once

**Downloaded file:**
- Format matches the transform output type (XML in this example)
- Filename is based on the output node name
- File is ready to use in other applications

---

### Workflow 2: Inputs Side Panel

This alternative workflow is faster when you already have files uploaded to Dexter or when adding to an existing mapping.

#### Step 1: Open inputs panel and select inputs

Instead of using the initialization modal, open the inputs side panel directly to access your uploaded files.

<img src="${inputspanel1Url}" alt="Inputs Panel 1" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Actions:**
1. Click the input icon (arrow pointing in) in the left control bar
2. The inputs side panel opens showing all available files
3. Browse through your uploaded files
4. Files are organized by type and collection

#### Step 2: Drag and drop inputs

Select the files you want to transform and drag them onto the canvas.

<img src="${inputspanel2Url}" alt="Inputs Panel 2" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**How to add inputs:**
1. Find your desired file in the inputs panel
2. Click and hold on the file name
3. Drag onto the canvas
4. Release to drop and create the input node

**Multiple inputs:**
- Drag files one at a time, or
- Drag an entire collection to add multiple inputs at once
- Position them manually as you add them

<img src="${inputspanel3Url}" alt="Inputs Panel 3" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Input node features:**
- Shows the file structure and data fields
- Displays connection handles for linking to transforms
- Can be repositioned by dragging
- Automatically parses the file content

#### Step 3: Add transform and output nodes

Follow the same process as in Workflow 1 to add transforms and outputs.

<img src="${inputspanel4Url}" alt="Inputs Panel 4" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Quick summary:**
1. Open transforms panel (click exchange arrows icon)
2. Drag transform node onto canvas
3. Connect input → transform → output
4. Wait for processing to complete (should be extremely fast)

**The result:**
- Input node connected to transform node
- Transform node connected to output node
- Complete data flow established
- Transformation processing automatically

#### Step 4: Download transformed data from output nodes

Once processing is complete, download your results.

<img src="${inputspanel5Url}" alt="Inputs Panel 5" style="max-width: 800px; height: auto; display: block; margin: 24px auto;" />

**Download options:**
- Click the download icon on the output node for individual download
- Or use the ZIP download button in the control bar for batch download

---

## Comparing the Two Workflows

### Initialization Modal (Workflow 1)

**Pros:**
- Guided step-by-step process
- Automatic node positioning
- Built-in file type detection
- Good for beginners
- Handles batch uploads efficiently

**Cons:**
- Less control over node placement
- Requires uploading files even if already uploaded
- More steps to complete

**Best for:**
- First-time users
- Starting a new mapping from scratch
- Uploading multiple files at once
- When you want automatic organization

### Inputs Side Panel (Workflow 2)

**Pros:**
- Faster workflow
- More control over node placement
- Reuses already-uploaded files
- Good for iterative work
- Fewer steps

**Cons:**
- Requires files to be uploaded first
- Manual node positioning
- Less guidance for beginners

**Best for:**
- Experienced users
- Adding to existing mappings
- Reusing files from previous work
- When you want precise control

`;
